import { Role, type GameState } from '@susdle/shared';
import type { CluegivingView, CluegivingPlayer, SecretWordTruth } from '../clue-giving/types.js';
import { classifyAvatarUrl } from './stateToView.js';
import { povFromRole } from './stateToMeetingView.js';

export interface CluegivingMappingContext {
  meId: string;
}

/**
 * Maps a clue-giving `GameState` to the local viewer's `CluegivingView`.
 *
 * POV stripping mirrors the server's `@view` filtering (handoff §3, lighter
 * than the meeting): `role`/`secretWord`/`secretWordLength` are SELF-tagged;
 * `ghostIds` is GHOSTS-tagged. `Player.clue` is PUBLIC — every alive client
 * sees every submitted clue — so it is never stripped; the schema's `''`
 * default is normalized to `null` (the view's "unwritten" sentinel).
 *
 * CRITICAL (handoff §2): players are sorted ascending by `turnOrder` at this
 * boundary so the cast grid's first cell is always the ghost-chosen first
 * speaker. The component renders the array as-is.
 */
export function stateToCluegivingView(state: GameState, ctx: CluegivingMappingContext): CluegivingView {
  const me = state.players.get(ctx.meId);
  const role = me?.role ?? Role.Villager;
  const isGhost = role === Role.Ghost;

  const players: CluegivingPlayer[] = [];
  for (const [, player] of state.players) {
    if (!player.alive) continue; // a clue-giving-time player is by definition alive (handoff types.ts)
    const avatar = classifyAvatarUrl(player.avatarUrl);
    const cluePlayer: CluegivingPlayer = {
      id: player.id,
      name: player.name,
      avatarUrl: player.avatarUrl,
      isDefaultAvatar: avatar.isDefaultAvatar,
      turnOrder: player.turnOrder,
      clue: player.clue === '' ? null : player.clue, // public; '' is the unwritten sentinel
      connected: player.connected,
    };
    if (avatar.defaultAvatarIndex !== undefined) {
      cluePlayer.defaultAvatarIndex = avatar.defaultAvatarIndex;
    }
    players.push(cluePlayer);
  }
  players.sort((a, b) => a.turnOrder - b.turnOrder);

  const secret: SecretWordTruth = isGhost
    ? { mode: 'length', value: me?.secretWordLength ?? 0 }
    : { mode: 'word', value: me?.secretWord ?? '' };

  return {
    meId: ctx.meId,
    pov: povFromRole(role),
    players,
    ghostIds: isGhost ? [...state.ghostIds] : [],
    currentClueGiverId: state.currentClueGiverId === '' ? null : state.currentClueGiverId,
    secret,
    phaseEndsAt: state.phaseEndsAt === 0 ? null : state.phaseEndsAt,
    /* Read straight off the wire — the server sets phaseDurationMs per-turn in
       advanceClueOrToDiscussion / transitionToPhase (14a D2); the dev builder
       fabricates it from the preset. No client-side config constant. */
    durationMs: state.phaseDurationMs,
  };
}
